import LogoComponent from "@/components/logo/LogoComponent";
import { NavigationItem } from "@/types/longhouseTypes";
import { gql, useQuery } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Search } from "lucide-react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MobileNavItem } from "./items/MobileNavItem";

const SEARCH_PLAYERS_QUERY = gql`
    query SearchPlayers($searchQuery: String, $limit: Int) {
        searchPlayers(searchQuery: $searchQuery, limit: $limit) {
            id
            name
            nickname
        }
    }
`;

interface Player {
    id: string;
    name: string;
    nickname: string;
}

interface MobileNavProps {
    sidebarOpen: boolean;
    setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
    navigation: NavigationItem[];
}

const MobileNav: React.FC<MobileNavProps> = ({
    sidebarOpen,
    setSidebarOpen,
    navigation,
}) => {
    const location = useLocation();
    const pathname = location.pathname;
    const navigate = useNavigate();
    const searchRef = useRef<HTMLDivElement>(null);
    const [playerSearch, setPlayerSearch] = useState("");
    const [showResults, setShowResults] = useState(false);

    const { data: searchData, loading: searchLoading } = useQuery<{
        searchPlayers: Player[];
    }>(SEARCH_PLAYERS_QUERY, {
        variables: {
            searchQuery: playerSearch,
            limit: 10,
        },
        skip: playerSearch.length < 2,
    });

    const handleLinkClick = () => {
        setSidebarOpen(false);
    };

    const handlePlayerClick = (playerId: string) => {
        navigate(`/dashboard/players/${playerId}`);
        setPlayerSearch("");
        setShowResults(false);
        setSidebarOpen(false);
    };

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                searchRef.current &&
                !searchRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <Transition.Root show={sidebarOpen} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50 lg:hidden"
                onClose={setSidebarOpen}
            >
                <div className="fixed inset-0 flex">
                    <Transition.Child
                        as={Fragment}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <Dialog.Panel className="relative flex w-full max-w-xs flex-1">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-in-out duration-300"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in-out duration-300"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                                <div className="absolute top-0 right-0 pt-5 pr-4">
                                    <button
                                        type="button"
                                        className="-m-2.5 p-2.5"
                                        onClick={() => setSidebarOpen(false)}
                                    >
                                        <span className="sr-only">
                                            Close sidebar
                                        </span>
                                        <XMarkIcon
                                            className="h-6 w-6 dark:text-white"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </div>
                            </Transition.Child>
                            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white pb-4 dark:bg-slate-800">
                                <div className="flex h-16 shrink-0 items-center pt-4">
                                    <Link
                                        to="/longhouse"
                                        className="mx-auto"
                                        onClick={handleLinkClick}
                                    >
                                        <LogoComponent className="mx-auto mt-4 h-12 w-auto" />
                                    </Link>
                                </div>

                                {/* Player Search */}
                                <div className="px-4 pb-4" ref={searchRef}>
                                    <div className="relative">
                                        <Search className="text-brand absolute top-2.5 left-3 h-4 w-4" />
                                        <input
                                            type="text"
                                            placeholder="Search Villagers..."
                                            value={playerSearch}
                                            onChange={(e) => {
                                                setPlayerSearch(e.target.value);
                                                setShowResults(true);
                                            }}
                                            onFocus={() => setShowResults(true)}
                                            className="bg-brand_charcoal border-brand text-brand placeholder-brand/60 focus:border-brand focus:ring-brand w-full rounded-lg border py-2 pr-4 pl-9 text-sm focus:ring-1 focus:outline-none"
                                        />
                                    </div>

                                    {/* Search Results Dropdown */}
                                    {showResults &&
                                        playerSearch.length >= 2 && (
                                            <div className="bg-brand_charcoal border-brand mt-1 max-h-64 overflow-y-auto rounded-lg border shadow-lg">
                                                {searchLoading && (
                                                    <div className="text-brand p-4 text-center text-sm">
                                                        Searching...
                                                    </div>
                                                )}
                                                {!searchLoading &&
                                                    searchData?.searchPlayers
                                                        .length === 0 && (
                                                        <div className="text-brand p-4 text-center text-sm">
                                                            No players found
                                                        </div>
                                                    )}
                                                {!searchLoading &&
                                                    searchData?.searchPlayers.map(
                                                        (player) => (
                                                            <button
                                                                key={player.id}
                                                                onClick={() =>
                                                                    handlePlayerClick(
                                                                        player.id,
                                                                    )
                                                                }
                                                                className="border-brand hover:bg-brand/10 w-full border-b p-3 text-left text-sm last:border-b-0"
                                                            >
                                                                <span className="text-brand font-semibold">
                                                                    {
                                                                        player.name
                                                                    }
                                                                </span>
                                                                {player.nickname && (
                                                                    <span className="text-brand/60 ml-2">
                                                                        (
                                                                        {
                                                                            player.nickname
                                                                        }
                                                                        )
                                                                    </span>
                                                                )}
                                                            </button>
                                                        ),
                                                    )}
                                            </div>
                                        )}
                                </div>

                                <nav className="flex flex-1 flex-col pt-4">
                                    <ul className="flex flex-1 flex-col gap-y-2">
                                        {navigation.map((navsection, index) => (
                                            <MobileNavItem
                                                key={index}
                                                item={navsection}
                                                isActive={
                                                    navsection.href === pathname
                                                }
                                                onClick={handleLinkClick}
                                                disabled={navsection.disabled}
                                            />
                                        ))}
                                    </ul>
                                </nav>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default MobileNav;
